import { Badge, Box, HStack, Text, VStack } from '@chakra-ui/react'
import {
  Background,
  type Edge,
  Handle,
  MarkerType,
  type Node,
  type NodeProps,
  Position,
  ReactFlow,
} from '@xyflow/react'
import { useMemo } from 'react'
import { useColorModeValue } from '~/color-mode'
import '@xyflow/react/dist/style.css'

/**
 * Deployed topology of the permanent demo, drawn left to right: what produces data,
 * what carries it, where it lands, and who reads it.
 *
 * Nodes are a custom Chakra component rather than React Flow's default node with an
 * inline `style`. That is deliberate: Chakra v3 only emits a --chakra-colors-* variable
 * for a token something actually references through Chakra, so a raw
 * var(--chakra-colors-purple-solid) inside an inline style resolves to nothing and the
 * accent silently disappears. Going through Chakra props keeps the diagram themed.
 *
 * The content reflects the running platform rather than the intended design - the two
 * have drifted more than once. Paths that are configured but not yet carrying data are
 * dashed rather than omitted, so the gap shows up here instead of mid-demo.
 */

type ServiceData = {
  title: string
  detail: string
  accent: string
  muted?: boolean
  wide?: boolean
}

function ServiceNode({ data }: NodeProps) {
  const { title, detail, accent, muted, wide } = data as ServiceData
  return (
    <>
      <Handle type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Box
        w={wide ? '230px' : '200px'}
        px={3}
        py={2}
        bg="bg.panel"
        borderWidth="1px"
        borderColor="border"
        borderLeftWidth="3px"
        borderLeftColor={accent}
        borderRadius="md"
        opacity={muted ? 0.6 : 1}
        boxShadow="xs"
        textAlign="left"
      >
        <Text fontSize="xs" fontWeight="bold" lineHeight="1.3">
          {title}
        </Text>
        <Text fontSize="10px" color="content.secondary" lineHeight="1.35">
          {detail}
        </Text>
      </Box>
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
    </>
  )
}

const nodeTypes = { service: ServiceNode }

const COL = { source: 0, edge: 300, broker: 620, store: 930, consumer: 1240 }

const ACCENT = {
  source: 'purple.solid',
  edge: 'orange.solid',
  broker: 'brand.solid',
  store: 'blue.solid',
  consumer: 'green.solid',
}

function svc(
  id: string,
  x: number,
  y: number,
  title: string,
  detail: string,
  accent: string,
  extra: { muted?: boolean; wide?: boolean } = {},
): Node {
  return { id, type: 'service', position: { x, y }, data: { title, detail, accent, ...extra } }
}

const nodes: Node[] = [
  // sources
  svc('recplay', COL.source, 40, 'RecPlay', 'OPC UA emulations · MQTT simulations', ACCENT.source),
  svc('sparkplug', COL.broker, -70, 'SparkplugB emulator', 'in-cluster · spBv1.0/#', ACCENT.source),
  // edge
  svc('edge-lisbon', COL.edge, 0, 'Edge · Lisbon', 'OPC UA → 184 packaging tags', ACCENT.edge),
  svc('edge-sm', COL.edge, 85, 'Edge · San Francisco, Tokyo', 'Modbus', ACCENT.edge),
  svc(
    'edge-energy',
    COL.edge,
    185,
    'Edge · Aberdeen, Houston',
    'OPC UA · energy sites',
    ACCENT.edge,
  ),
  // brokers
  svc(
    'central',
    COL.broker,
    40,
    'Central broker · 3 nodes',
    'Smart Manufacturing only',
    ACCENT.broker,
    { wide: true },
  ),
  svc(
    'shared',
    COL.broker,
    230,
    'Shared broker · 3 nodes',
    'Industry verticals · energy',
    ACCENT.broker,
    { wide: true },
  ),
  // storage and integration
  svc(
    'ts-packaging',
    COL.store,
    -50,
    'TimescaleDB · packaging',
    'mqtt_raw · 1m aggregate',
    ACCENT.store,
  ),
  svc('ts-metrics', COL.store, 40, 'TimescaleDB · metrics', 'BBCorp/…/metric/+', ACCENT.store),
  svc('kafka', COL.store, 130, 'Kafka', '6 mqtt-to-kafka mappings', ACCENT.store),
  svc('ts-energy', COL.store, 230, 'TimescaleDB · energy', 'ecorp/#', ACCENT.store),
  svc('cloud', COL.store, 320, 'HiveMQ Platform (cloud)', 'bridge · TLS', ACCENT.store),
  // consumers
  svc('grafana', COL.consumer, 0, 'Grafana', 'packaging · energy · site KPIs', ACCENT.consumer),
  svc('stories', COL.consumer, 95, 'Stories portal', 'this app · MQTT over WSS', ACCENT.consumer),
  svc('watchdog', COL.consumer, 190, 'Watchdog', 'every 5 min · health table', ACCENT.consumer),
  svc('llm', COL.consumer, 285, 'Local LLM', 'off-cluster VM · vLLM · L4', ACCENT.consumer, {
    muted: true,
  }),
]

// Node colours come from Chakra tokens and follow the theme on their own. Edge strokes
// and the background dots cannot - React Flow wants concrete values - so they are the one
// thing here that has to be resolved against the active colour mode. Defined as data and
// built in the component, rather than baked in at module scope where a single literal
// would be too dark on dark or too pale on light.
type EdgeDef = { id: string; source: string; target: string; label?: string; pending?: boolean }

const EDGE_DEFS: EdgeDef[] = [
  { id: 'e1', source: 'recplay', target: 'edge-lisbon', label: 'OPC UA' },
  { id: 'e2', source: 'recplay', target: 'edge-energy', label: 'OPC UA' },
  {
    id: 'e3',
    source: 'recplay',
    target: 'shared',
    label: 'MQTT · pending run restart',
    pending: true,
  },
  { id: 'e4', source: 'sparkplug', target: 'central', label: 'spBv1.0' },
  { id: 'e4b', source: 'recplay', target: 'central', label: 'MQTT · live runs' },
  { id: 'e5', source: 'edge-lisbon', target: 'central', label: 'bridge' },
  { id: 'e6', source: 'edge-sm', target: 'central', label: 'bridge' },
  { id: 'e7', source: 'edge-energy', target: 'shared', label: 'bridge' },
  { id: 'e8', source: 'central', target: 'ts-packaging', label: 'PostgreSQL ext' },
  { id: 'e9', source: 'central', target: 'ts-metrics' },
  { id: 'e10', source: 'central', target: 'kafka', label: 'Kafka ext' },
  { id: 'e11', source: 'central', target: 'cloud', label: 'bridge' },
  { id: 'e12', source: 'shared', target: 'ts-energy', label: 'PostgreSQL ext' },
  { id: 'e13', source: 'ts-packaging', target: 'grafana', label: 'SQL' },
  { id: 'e14', source: 'ts-metrics', target: 'grafana' },
  { id: 'e15', source: 'ts-energy', target: 'grafana' },
  { id: 'e16', source: 'central', target: 'stories', label: 'WSS' },
  { id: 'e17', source: 'ts-packaging', target: 'watchdog' },
]

function buildEdges(stroke: string, labelFill: string): Edge[] {
  return EDGE_DEFS.map(({ id, source, target, label, pending }) => ({
    id,
    source,
    target,
    label,
    type: 'smoothstep' as const,
    animated: pending,
    markerEnd: { type: MarkerType.ArrowClosed, color: stroke },
    style: {
      stroke,
      strokeWidth: 1.5,
      ...(pending ? { strokeDasharray: '4 4' } : {}),
    },
    labelStyle: { fontSize: 10, fill: labelFill },
    labelShowBg: false,
  }))
}

const legend = [
  { accent: ACCENT.source, text: 'Sources' },
  { accent: ACCENT.edge, text: 'Edge' },
  { accent: ACCENT.broker, text: 'Brokers' },
  { accent: ACCENT.store, text: 'Storage & integration' },
  { accent: ACCENT.consumer, text: 'Consumers' },
]

export default function PlatformArchitecture() {
  const stroke = useColorModeValue('#64748b', '#8b93a1')
  const labelFill = useColorModeValue('#475569', '#a1a8b5')
  const dots = useColorModeValue('#cbd5e1', '#3f3f46')
  // Explicit rather than a Chakra token: xyflow's Background paints its own rect and
  // the imported stylesheet paints .react-flow, so both had to be set to stop the
  // canvas staying white on the dark theme.
  const canvasBg = useColorModeValue('#fafafa', '#0f1216')
  const edges = useMemo(() => buildEdges(stroke, labelFill), [stroke, labelFill])

  return (
    <VStack align="stretch" gap={3} w="100%">
      <HStack gap={4} flexWrap="wrap">
        {legend.map((l) => (
          <HStack key={l.text} gap={2}>
            <Box w="10px" h="10px" borderRadius="sm" bg={l.accent} />
            <Text fontSize="xs" color="content.secondary">
              {l.text}
            </Text>
          </HStack>
        ))}
        <Badge size="sm" variant="outline">
          dashed = configured, not yet publishing
        </Badge>
      </HStack>
      <Box
        h="480px"
        w="100%"
        borderWidth="1px"
        borderRadius="md"
        overflow="hidden"
        style={{ background: canvasBg }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          fitView
          fitViewOptions={{ padding: 0.14 }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          proOptions={{ hideAttribution: true }}
          // @xyflow/react/dist/style.css paints .react-flow with a light background, which
          // stayed white on the dark theme. Transparent defers to the Chakra Box below,
          // so the canvas follows the colour mode without duplicating the token here.
          style={{ background: canvasBg }}
          // A diagram, not an editor: the page keeps scrolling when the pointer crosses
          // it, instead of the canvas swallowing the wheel.
          zoomOnScroll={false}
          panOnScroll={false}
          preventScrolling={false}
        >
          <Background gap={18} size={1} color={dots} bgColor={canvasBg} />
        </ReactFlow>
      </Box>
    </VStack>
  )
}
